/**
 * @file notebook execute_result 运行结果组件
 * @author FranckChen(chenfan02@baidu.com)
 */

import React, {useCallback, memo} from 'react';
import {crlf} from 'eol';
import {BlockMath, InlineMath} from 'react-katex';
import useToggle from 'react-use/lib/useToggle';
import { getTextInfo } from './../../../../../../utils/getTextInfo';

import {IOutput} from '../../types/codeCell';

interface IProps {
    output: IOutput;
}

const ExecuteResult: React.FC<IProps> = memo(props => {
    const data = props.output.data || {};

    const [trustJS, toggleTrustJS] = useToggle(false);

    const katexErrorHandler = useCallback(() => {
        return (
            <div className="ai-np-execute-result-tex-err-tip">
                抱歉，数学公式解析失败，可能是拼写错误或暂不支持。
            </div>
        );
    }, []);

    const getProps = getTextInfo(data['image/svg+xml']?.join('') ?? '');

    return (
        <div className="ai-np-execute-result">
            {
                !!data['image/jpeg']
                && <img src={`data:image/jpeg;base64,${data['image/jpeg']}`}
                        alt=""
                        className="ai-np-img" />
            }
            {
                !!data['image/png']
                && <img src={`data:image/png;base64,${data['image/png']}`}
                        alt=""
                        className="ai-np-img" />
            }
            {
                !!data['image/gif']
                && <img src={`data:image/gif;base64,${data['image/gif']}`}
                        alt=""
                        className="ai-np-img" />
            }
            {
                !!data['image/svg+xml']
                && <div {...getProps} />
            }
            {
                !!data['application/pdf'] && (
                    <a target="_blank"
                       rel="noopener noreferrer"
                       href={`data:application/pdf;base64,${data['application/pdf']}`}>
                        下载PDF
                    </a>
                )
            }
            {
                !!data['text/plain'] && (
                    <div>
                        <pre className="ai-np-pre">{data['text/plain']}</pre>
                    </div>
                )
            }
            {
                (() => {
                    if (!Array.isArray(data['text/latex'])) {
                        return null;
                    }
                    // data['text/latex']
                    const latex: string = crlf(data['text/latex'].join(''));

                    let type: 'inline' | 'block' | undefined;
                    if (latex[0] === '$' && latex[1] === '$') {
                        type = 'block';
                    }
                    else if (latex[0] === '$') {
                        type = 'inline';
                    }
                    else {
                        type = 'inline';
                    }

                    const content: string = latex.replace(/^\$\$?|\$\$?$|/g, '');
                    if (type === 'block') {
                        return <BlockMath
                            math={content}
                            renderError={katexErrorHandler}
                        />;
                    }
                    else if (type === 'inline') {
                        return <InlineMath math={content}
                                           renderError={katexErrorHandler} />;
                    }
                })()
            }
            {
                (() => {
                    if (!Array.isArray(data['text/html'])) {
                        return null;
                    }

                    const raw: string = crlf(data['text/html'].join(''));
                    if (raw) {
                        return;
                    }

                    const divEl: HTMLDivElement = document.createElement('div');
                    divEl.innerHTML = raw;
                    const scriptEls: Element[] = Array.from(divEl.querySelectorAll('script'));
                    const nonScriptEls: Element[] = Array.from<Element>(divEl.children).filter(el => {
                        return !(el.tagName.toLowerCase() === 'script');
                    });

                    return (
                        <div
                            ref={parentEl => {
                                if (parentEl) {
                                    nonScriptEls.forEach(el => parentEl.append(el));
                                    scriptEls.forEach(el => parentEl.append(el));
                                }
                            }}
                        />
                    );
                })()
            }
            {
                (() => {
                    if (!Array.isArray(data['application/javascript'])) {
                        return null;
                    }

                    const rawScript: string = crlf(data['application/javascript'].join(''));
                    if (!rawScript) {
                        return;
                    }
                    if (!trustJS) {
                        return (
                            <div className="ai-np-secure-tip">
                                输出中存在可执行的javascript代码，出于安全性考虑，
                                已自动将其屏蔽,如确认代码没有安全性问题，希望继续浏览，请点击
                                <span className="ai-np-secure-confirm"
                                      onClick={() => toggleTrustJS(true)}>
                                    确认
                                </span>
                            </div>
                        );
                    }

                    return (
                        <React.Fragment>
                            <div
                                ref={el => {
                                    if (!el) {
                                        return;
                                    }

                                    const scriptEl: Element = document.createElement('script');
                                    scriptEl.innerHTML = rawScript;
                                    el.append(scriptEl);
                                }}
                            />
                            <div className="ai-np-secure-js-title">
                                javascript代码:
                            </div>
                            <div className="ai-np-secure-js-code">
                                {data['application/javascript']}
                            </div>
                        </React.Fragment>
                    );
                })()
            }
        </div>
    );
});

export default ExecuteResult;
